import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import {
  Container,
  Paper,
  Typography,
  Grid,
  Box,
  Card,
  CardContent,
  Chip,
  Button,
  IconButton,
  Avatar,
  Alert,
  CircularProgress,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Stack,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Divider,
  TextField,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Favorite as FavoriteIcon,
  TrendingUp as TrendingUpIcon,
  Inventory as InventoryIcon,
  Euro as EuroIcon,
  Category as CategoryIcon,
  QrCode as QrCodeIcon,
  LocalOffer as LocalOfferIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
  Store as StoreIcon,
  BrandingWatermark as BrandingWatermarkIcon,
  LocationOn as LocationOnIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  ShoppingCart as ShoppingCartIcon
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Barcode from 'react-barcode';
import html2canvas from 'html2canvas';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';
import axiosInstance from '../../../core/api/interceptors/axiosInterceptor';
import { deleteProduct, getProductPurchaseHistory, getProductById } from '../services/productService';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ProductWishlistDialog from '../../../components/ProductWishlistDialog';
import { useDispatch, useSelector } from 'react-redux';
import { fetchVariantsByProduct, removeVariant } from '../../../app/store/slices/productVariantSlice';
import VariantFormDialog from '../components/VariantFormDialog';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import InventoryItemsManagementDialog from '../../inventory/components/InventoryItemsManagementDialog';

const PRODUCT_VARIANTS_ENABLED = false;

// ─── Tape / label size presets ───────────────────────────────────────────────
const TAPE_SIZES = [
  { id: 'dk22205',       label: 'DK-22205 — 62mm Continuous',    width: 62,  height: 'auto' },
  { id: 'dk11202',       label: 'DK-11202 — 62mm × 100mm',       width: 62,  height: 100   },
  { id: 'dk11201',       label: 'DK-11201 — 29mm × 90mm',        width: 29,  height: 90    },
  { id: 'dk22210',       label: 'DK-22210 — 29mm Continuous',    width: 29,  height: 'auto' },
  { id: 'dk11204',       label: 'DK-11204 — 17mm × 54mm',        width: 17,  height: 54    },
  { id: 'thermal57x32',  label: 'Thermal — 57mm × 32mm',         width: 57,  height: 32    },
  { id: 'thermal58auto', label: 'Thermal — 58mm Continuous',     width: 58,  height: 'auto' },
  { id: 'shipping',      label: 'Shipping — 100mm × 150mm',      width: 100, height: 150   },
  { id: 'custom',        label: 'Custom Size…',                  width: null, height: null  },
];

/**
 * Returns barcode widget dimensions and print CSS values scaled to the tape width.
 * Also detects portrait orientation for adaptive layout.
 */
const getLabelConfig = (widthMm, heightMm, hasPrice) => {
  // Detect portrait orientation (height > width)
  const isPortrait = heightMm !== 'auto' && heightMm > widthMm * 1.2;
  
  // Calculate adaptive font sizes based on width (scale with label width)
  // Use a more aggressive scaling for narrow labels to fill space better
  const baseFontSize = Math.max(10, Math.round(widthMm * 0.35)); // Increased from 0.22 to 0.35 for larger text
  const nameFontSize = hasPrice ? `${baseFontSize}px` : `${baseFontSize + 3}px`;
  
  // For very narrow labels (portrait), use extra compact settings
  if (widthMm <= 20) return {
    barcodeWidth: 0.8,  barcodeHeight: hasPrice ? 18 : 26,
    fontSize: isPortrait ? '9px' : '10px',  
    nameMaxH: hasPrice ? '6mm'  : '9mm',   
    barcodeMaxH: hasPrice ? '9mm'  : '13mm',
    isPortrait,         
    nameLines: 2,
    nameFontSize: isPortrait ? '9px' : '10px',
  };
  if (widthMm <= 35) return {
    barcodeWidth: isPortrait ? 1.2 : 1.6,  barcodeHeight: hasPrice ? 32 : 46,
    fontSize: isPortrait ? '11px' : '13px',  
    nameMaxH: hasPrice ? '10mm'  : '16mm',  
    barcodeMaxH: hasPrice ? '14mm' : '22mm',
    isPortrait,         
    nameLines: isPortrait ? 3 : 2,
    nameFontSize,
  };
  if (widthMm <= 70) return {
    barcodeWidth: hasPrice ? 1.8 : 2.2, barcodeHeight: hasPrice ? 45 : 60,
    fontSize: hasPrice ? '14px' : '16px', 
    nameMaxH: hasPrice ? '15mm' : '22mm', 
    barcodeMaxH: hasPrice ? '18mm' : '26mm',
    isPortrait,         
    nameLines: 2,
    nameFontSize,
  };
  return {
    barcodeWidth: 2.6,  barcodeHeight: hasPrice ? 60 : 80,
    fontSize: hasPrice ? '16px' : '18px', 
    nameMaxH: hasPrice ? '22mm' : '34mm', 
    barcodeMaxH: hasPrice ? '28mm' : '42mm',
    isPortrait: false,  
    nameLines: 2,
    nameFontSize,
  };
};
// ─────────────────────────────────────────────────────────────────────────────

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  
  // Try to get product data from navigation state first
  const navigationData = location.state?.productData;
  
  const [product, setProduct] = useState(navigationData || null);
  const [loading, setLoading] = useState(!navigationData);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);
  const [barcodeDialogOpen, setBarcodeDialogOpen] = useState(false);
  const [wishlistDialogOpen, setWishlistDialogOpen] = useState(false);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [purchaseHistoryLoading, setPurchaseHistoryLoading] = useState(false);

  // Variants tab state
  const dispatch = useDispatch();
  const { variantsByProduct, loading: variantsLoading, submitting: variantsSubmitting } = useSelector((s) => s.productVariants || {});
  const [variantDialogOpen, setVariantDialogOpen] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [confirmDeleteVariant, setConfirmDeleteVariant] = useState(null);
  const productVariantsData = variantsByProduct?.[id] || null;

  // Inventory Items Management
  const [inventoryDialogOpen, setInventoryDialogOpen] = useState(false);
  const [selectedVariantForInventory, setSelectedVariantForInventory] = useState(null);

  // Barcode label options
  const [includePrice, setIncludePrice] = useState(true);
  const [barcodeType, setBarcodeType] = useState('barcode'); // 'barcode' or 'sku'
  const [editableProductName, setEditableProductName] = useState('');

  // Tape / label size
  const [tapeSize, setTapeSize] = useState('dk22205');
  const [customWidth, setCustomWidth] = useState(62);
  const [customHeightAuto, setCustomHeightAuto] = useState(true);
  const [customHeightValue, setCustomHeightValue] = useState(50);

  // Mock analytics data (replace with real API calls)
  const [salesData] = useState([
    { month: 'Jan', sales: 1, revenue: 1 },
    { month: 'Feb', sales: 1, revenue: 1 },
    { month: 'Mar', sales: 1, revenue: 1 },
    { month: 'Apr', sales: 1, revenue: 1 },
    { month: 'May', sales: 1, revenue: 1 },
    { month: 'Jun', sales: 1, revenue: 1 }
  ]);

  const [purchaseData] = useState([
    { month: 'Jan', purchases: 45, cost: 675 },
    { month: 'Feb', purchases: 52, cost: 780 },
    { month: 'Mar', purchases: 48, cost: 720 },
    { month: 'Apr', purchases: 61, cost: 915 },
    { month: 'May', purchases: 55, cost: 825 },
    { month: 'Jun', purchases: 67, cost: 1005 }
  ]);

  const [stockMovement] = useState([
    { date: '2024-01', inbound: 100, outbound: 45, balance: 155 },
    { date: '2024-02', inbound: 50, outbound: 52, balance: 153 },
    { date: '2024-03', inbound: 75, outbound: 48, balance: 180 },
    { date: '2024-04', inbound: 60, outbound: 61, balance: 179 },
    { date: '2024-05', inbound: 80, outbound: 55, balance: 204 },
    { date: '2024-06', inbound: 40, outbound: 67, balance: 177 }
  ]);

  const [performanceMetrics] = useState([
    { name: 'Total Sales', value: 396, color: '#8884d8' },
    { name: 'Returns', value: 12, color: '#82ca9d' },
    { name: 'Exchanges', value: 8, color: '#ffc658' },
    { name: 'Damages', value: 3, color: '#ff7300' }
  ]);

  // ✅ Load product data - Fixed to always fetch if no navigation data
  useEffect(() => {
    if (!navigationData && id) {
      loadProductFromAPI();
    }
  }, [id, navigationData]);

  // Load analytics data
  useEffect(() => {
    if (id) {
      loadProductAnalytics();
    }
  }, [id]);

  const loadProductFromAPI = async () => {
    try {
      setLoading(true);
      
      const response = await getProductById(id);
      
      if (response.status === 'Success' && response.payload) {
        setProduct(response.payload);
      } else if (response) {
        // Handle case where response doesn't have status/payload structure
        setProduct(response);
      } else {
        throw new Error('Product not found');
      }
    } catch (error) {

      toast.error('Failed to load product details');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const loadProductAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      // Replace with actual API call
      // const response = await axiosInstance.get(`Products/${id}/analytics`);
      
      // Mock analytics data
      setTimeout(() => {
        setAnalytics({
          totalSales: 1,
          totalRevenue: 100,
          averageOrderValue: 30,
          stockTurnover: 4.2,
          profitMargin: 45.5,
          lastSaleDate: '2024-06-15',
          topCustomers: [
            { name: 'John Doe', orders: 15, total: 450 },
            { name: 'Jane Smith', orders: 12, total: 360 },
            { name: 'Bob Johnson', orders: 8, total: 240 }
          ]
        });
        setAnalyticsLoading(false);
      }, 1000);
    } catch (error) {

      setAnalyticsLoading(false);
    }
  };

  const loadPurchaseHistory = async () => {
    if (!id) return;
    
    try {
      setPurchaseHistoryLoading(true);
      const response = await getProductPurchaseHistory(id);
      
      if (response.status === 'Success' && response.payload) {
        setPurchaseHistory(response.payload);
      } else if (Array.isArray(response)) {
        setPurchaseHistory(response);
      } else {
        setPurchaseHistory([]);
      }
    } catch (error) {

      toast.error('Failed to load purchase history');
      setPurchaseHistory([]);
    } finally {
      setPurchaseHistoryLoading(false);
    }
  };

  // Load purchase history when tab is switched to index 1, variants to index 2
  useEffect(() => {
    if (PRODUCT_VARIANTS_ENABLED && currentTab === 2 && !productVariantsData) {
      dispatch(fetchVariantsByProduct(id));
    }
    if (currentTab === 1 && purchaseHistory.length === 0) {
      loadPurchaseHistory();
    }
  }, [currentTab]);

  const handleEdit = () => {
    navigate(`/products/edit/${id}`, {
      state: { productData: product }
    });
  };

  const handleOpenBarcodeDialog = () => {
    setEditableProductName(product?.Name || '');
    setBarcodeDialogOpen(true);
  };

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete "${product?.Name}"?`)) {
      try {
        await deleteProduct(id);
        toast.success('Product deleted successfully');
        navigate('/products');
      } catch (error) {

        toast.error('Failed to delete product');
      }
    }
  };

  const handlePrintBarcode = async () => {
    const barcodeElement = document.getElementById('barcode-to-print');
    if (!barcodeElement) {
      toast.error('Barcode element not found');
      return;
    }

    try {
      // Resolve effective dimensions from selected tape
      const tapeCfg = TAPE_SIZES.find(t => t.id === tapeSize);
      const effectiveWidth = tapeSize === 'custom' ? (customWidth || 62) : (tapeCfg?.width || 62);
      const effectiveHeight = tapeSize === 'custom'
        ? (customHeightAuto ? 'auto' : (customHeightValue || 50))
        : (tapeCfg?.height ?? 'auto');

      const printCfg = getLabelConfig(effectiveWidth, effectiveHeight, includePrice);
      const priceFontPx = Math.max(12, Math.round(effectiveWidth * 0.40));
      const layoutGap = printCfg.isPortrait ? '3mm' : (includePrice ? '4mm' : '5mm');

      // Create temporary container for rendering
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0';
      tempContainer.style.width = `${effectiveWidth}mm`;
      
      // Set height for portrait labels to ensure proper vertical centering
      if (effectiveHeight !== 'auto') {
        tempContainer.style.height = `${effectiveHeight}mm`;
      }
      
      tempContainer.style.backgroundColor = '#ffffff';
      tempContainer.style.padding = '3mm';
      tempContainer.style.display = 'flex';
      tempContainer.style.flexDirection = 'column';
      tempContainer.style.alignItems = 'center';
      tempContainer.style.justifyContent = 'center';
      tempContainer.style.gap = layoutGap;
      tempContainer.style.fontFamily = 'Arial, sans-serif';

      // Add product name
      const nameDiv = document.createElement('div');
      nameDiv.textContent = editableProductName;
      nameDiv.style.fontSize = printCfg.nameFontSize;
      nameDiv.style.fontWeight = 'bold';
      nameDiv.style.textAlign = 'center';
      nameDiv.style.width = '100%';
      nameDiv.style.wordWrap = 'break-word';
      nameDiv.style.lineHeight = '1.2';
      tempContainer.appendChild(nameDiv);

      // Add barcode (clone from preview)
      const barcodeClone = barcodeElement.cloneNode(true);
      barcodeClone.style.display = 'flex';
      barcodeClone.style.justifyContent = 'center';
      barcodeClone.style.alignItems = 'center';
      tempContainer.appendChild(barcodeClone);

      // Add price if included
      if (includePrice) {
        const priceDiv = document.createElement('div');
        priceDiv.textContent = `€${Number(product.SalePrice || product.Price || 0).toFixed(2)}`;
        priceDiv.style.fontSize = `${priceFontPx}px`;
        priceDiv.style.fontWeight = 'bold';
        priceDiv.style.textAlign = 'center';
        priceDiv.style.width = '100%';
        tempContainer.appendChild(priceDiv);
      }

      document.body.appendChild(tempContainer);

      // Wait for rendering
      await new Promise(resolve => setTimeout(resolve, 500));

      // Convert to canvas with high quality
      const canvas = await html2canvas(tempContainer, {
        scale: 3,
        backgroundColor: '#ffffff',
        logging: false,
        width: tempContainer.offsetWidth,
        height: tempContainer.offsetHeight,
      });

      // Remove temporary container
      document.body.removeChild(tempContainer);

      // Convert canvas to image
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      // Calculate dimensions in mm at 96 DPI (standard screen DPI)
      const mmWidth = effectiveWidth;
      const mmHeight = (imgHeight / imgWidth) * mmWidth;

      const isContinuous = effectiveHeight === 'auto';
      const pageSize = isContinuous ? `${mmWidth}mm auto` : `${mmWidth}mm ${effectiveHeight}mm`;

      // Create print window with image
      const printWindow = window.open('', '', 'width=400,height=300');
      if (!printWindow) {
        toast.error('Could not open print window');
        return;
      }

      printWindow.document.write(`
        <html>
          <head>
            <title>Print Label - ${editableProductName}</title>
            <style>
              @page {
                size: ${pageSize};
                margin: 0;
              }
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              html, body {
                width: ${mmWidth}mm;
                ${isContinuous ? 'height: auto;' : `height: ${effectiveHeight}mm;`}
                margin: 0;
                padding: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                overflow: hidden;
              }
              img {
                display: block;
                width: 100%;
                height: ${isContinuous ? 'auto' : '100%'};
                object-fit: contain;
                object-position: center center;
              }
              @media print {
                html, body {
                  width: ${mmWidth}mm;
                  ${isContinuous ? 'height: auto;' : `height: ${effectiveHeight}mm;`}
                }
                img {
                  page-break-inside: avoid;
                  page-break-after: avoid;
                }
              }
            </style>
          </head>
          <body>
            <img src="${imgData}" alt="Product Label" />
          </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.focus();

      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);

    } catch (error) {

      toast.error('Failed to print barcode label');
    }
  };

  const TabPanel = ({ children, value, index, ...other }) => (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`product-tabpanel-${index}`}
      aria-labelledby={`product-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">t('product_not_found')</Alert>
        <Button 
          variant="contained" 
          onClick={() => navigate('/products')}
          sx={{ mt: 2 }}
        >
          t('back_to_products')
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 1, sm: 2 }, mb: 4, px: { xs: 1, sm: 2, md: 3 }, width: '100%', minWidth: 0 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
          <IconButton onClick={() => navigate('/products')} sx={{ color: 'primary.main' }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main', fontSize: { xs: '1.75rem', sm: '2.125rem' }, overflowWrap: 'anywhere' }}>
              {product.Name || product.ProductName || t('product_details')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              SKU: {product.SKU || 'N/A'} • {t('product_details')}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: { xs: '100%', md: 'auto' } }}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handleOpenBarcodeDialog}
            disabled={!product.Barcode && !product.BarCode && !product.SKU}
            sx={{ borderRadius: 2, flex: { xs: '1 1 100%', md: '0 0 auto' } }}
          >
            {t('print_label') || 'Print Label'}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<FavoriteIcon />}
            onClick={() => setWishlistDialogOpen(true)}
            sx={{ borderRadius: 2, borderColor: 'error.main', color: 'error.main', flex: { xs: '1 1 100%', md: '0 0 auto' }, '&:hover': { bgcolor: 'error.50' } }}
          >
            {t('wishlist') || 'Wishlist'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            sx={{ borderRadius: 2, flex: { xs: '1 1 100%', md: '0 0 auto' } }}
          >
            {t('edit') || 'Edit'}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            sx={{ borderRadius: 2, flex: { xs: '1 1 100%', md: '0 0 auto' } }}
          >
            {t('delete')}
          </Button>
        </Box>
      </Box>

      {/* Tabs for Product Details */}
      <Paper sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={currentTab} 
            onChange={(e, newValue) => setCurrentTab(newValue)}
            sx={{ px: { xs: 1, sm: 3 } }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label={t('product_info')} />
            <Tab label="Purchase History" icon={<HistoryIcon />} iconPosition="start" />
            {PRODUCT_VARIANTS_ENABLED && <Tab label="Variants" icon={<InventoryIcon />} iconPosition="start" />}
          </Tabs>
        </Box>

        {/* t('product_details') Tab */}
        <TabPanel value={currentTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {t('basic_information') || 'Basic Information'}
              </Typography>
              
              <List>
                <ListItem>
                  <ListItemIcon><CategoryIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary={t('category')} 
                    secondary={
                      product.CategoryName || 
                      product.Category?.CategoryName || 
                      product.ProductCategory?.CategoryName || 
                      product.category?.name || 
                      t('no_category')
                    } 
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon><QrCodeIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary={t('barcode')} 
                    secondary={product.Barcode || product.BarCode || t('not_available')}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon><BusinessIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary={t('supplier')} 
                    secondary={
                      product.SupplierName || 
                      product.Supplier?.Name || 
                      product.supplier?.name || 
                      t('unknown')
                    } 
                  />
                </ListItem>

                {product.BrandName && (
                  <ListItem>
                    <ListItemIcon><BrandingWatermarkIcon color="primary" /></ListItemIcon>
                    <ListItemText 
                      primary={t('brand')} 
                      secondary={product.BrandName || t('unknown')} 
                    />
                  </ListItem>
                )}

                {product.ModelNumber && (
                  <ListItem>
                    <ListItemIcon><StoreIcon color="primary" /></ListItemIcon>
                    <ListItemText 
                      primary={t('model_number')} 
                      secondary={product.ModelNumber} 
                    />
                  </ListItem>
                )}

                <ListItem>
                  <ListItemIcon><LocationOnIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary={t('location') || 'Location'} 
                    secondary={product?.Location || t('not_specified')} 
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    {product.IsActive ? 
                      <CheckCircleIcon color="success" /> : 
                      <WarningIcon color="error" />
                    }
                  </ListItemIcon>
                  <ListItemText 
                    primary={t('status')} 
                    secondary={
                      <Chip 
                        label={product.IsActive ? t('active') : t('inactive')} 
                        color={product.IsActive ? 'success' : 'error'}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    } 
                  />
                </ListItem>
              </List>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {t('pricing_and_stock') || 'Pricing and Stock'}
              </Typography>
              
              <List>
                <ListItem>
                  <ListItemText 
                    primary={t('cost_price')} 
                    secondary={`€${Number(product.CostPrice || 0).toFixed(2)}`}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemText 
                    primary={t('sale_price')} 
                    secondary={`€${Number(product.SalePrice || product.Price || 0).toFixed(2)}`}
                  />
                </ListItem>

                {product.MinSalePrice > 0 && (
                  <ListItem>
                    <ListItemText 
                      primary={t('min_sale_price')} 
                      secondary={`€${Number(product.MinSalePrice || 0).toFixed(2)}`}
                    />
                  </ListItem>
                )}

                {product.AveragePrice > 0 && (
                  <ListItem>
                    <ListItemText 
                      primary={t('average_price')} 
                      secondary={`€${Number(product.AveragePrice || 0).toFixed(2)}`}
                    />
                  </ListItem>
                )}
                
                <ListItem>
                  <ListItemText 
                    primary={t('current_stock')} 
                    secondary={product.StockQuanity || product.StockQuantity || 0}
                  />
                </ListItem>

                <ListItem>
                  <ListItemText 
                    primary={t('min_stock_level')} 
                    secondary={product.MinStockLevel || 0}
                  />
                </ListItem>
              </List>

              {product.IsPromotionProduct && (
                <Box sx={{ mt: 2 }}>
                  <Chip 
                    icon={<LocalOfferIcon />}
                    label={`${product.PromotionPercentage || 0}% ${t('off')} - ${t('promotion_active')}`}
                    color="error"
                    variant="filled"
                  />
                </Box>
              )}
            </Grid>

            {product.Description && (
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  {t('description') || 'Description'}
                </Typography>
                <Typography 
                  variant="body1" 
                  color="text.secondary"
                  sx={{
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    whiteSpace: 'pre-wrap',
                    maxWidth: '100%'
                  }}
                >
                  {product.Description}
                </Typography>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        {/* Purchase History Tab */}
        <TabPanel value={currentTab} index={1}>
          {purchaseHistoryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : purchaseHistory.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <ShoppingCartIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Purchase History
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This product has not been purchased yet.
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="h6" fontWeight="bold" mb={3}>
                Purchase History
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: '#f9fafb', borderRadius: 2 }}>
                    <Grid container spacing={2}>
                      {purchaseHistory[0] && (
                        <>
                          <Grid item xs={12} sm={6} md={3}>
                            <Box>
                              <Typography variant="body2" color="text.secondary">Latest Purchase Price</Typography>
                              <Typography variant="h6" fontWeight="bold" color="primary">
                                €{purchaseHistory[0].FinalUnitCost?.toFixed(2) || '0.00'}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Box>
                              <Typography variant="body2" color="text.secondary">Average Cost</Typography>
                              <Typography variant="h6" fontWeight="bold">
                                €{purchaseHistory[0].AverageCostAfterPurchase?.toFixed(2) || '0.00'}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Box>
                              <Typography variant="body2" color="text.secondary">Total Purchases</Typography>
                              <Typography variant="h6" fontWeight="bold">
                                {purchaseHistory.length}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Box>
                              <Typography variant="body2" color="text.secondary">Last Supplier</Typography>
                              <Typography variant="h6" fontWeight="bold">
                                {purchaseHistory[0].SupplierName || 'N/A'}
                              </Typography>
                            </Box>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper sx={{ overflow: 'hidden', borderRadius: 2 }}>
                    <Box sx={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f9fafb' }}>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '0.875rem', fontWeight: 700, color: '#374151' }}>Date</th>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '0.875rem', fontWeight: 700, color: '#374151' }}>Invoice #</th>
                            <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '0.875rem', fontWeight: 700, color: '#374151' }}>Supplier</th>
                            <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '0.875rem', fontWeight: 700, color: '#374151' }}>Type</th>
                            <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '0.875rem', fontWeight: 700, color: '#374151' }}>Store</th>
                            <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '0.875rem', fontWeight: 700, color: '#374151' }}>Qty</th>
                            <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '0.875rem', fontWeight: 700, color: '#374151' }}>Unit Cost</th>
                            <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '0.875rem', fontWeight: 700, color: '#374151' }}>Discount</th>
                            <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '0.875rem', fontWeight: 700, color: '#374151' }}>Tax</th>
                            <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '0.875rem', fontWeight: 700, color: '#374151' }}>Final Unit Cost</th>
                            <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '0.875rem', fontWeight: 700, color: '#374151' }}>Total Cost</th>
                            <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '0.875rem', fontWeight: 700, color: '#374151' }}>Price Change</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            // Find lowest and highest purchase prices
                            const prices = purchaseHistory.History.map(item => item.FinalUnitCost || 0);
                            const minPrice = Math.min(...prices);
                            const maxPrice = Math.max(...prices);
                            
                            return purchaseHistory.History.map((item, idx) => {
                              const itemPrice = item.FinalUnitCost || 0;
                              const isLowest = itemPrice === minPrice && minPrice > 0;
                              const isHighest = itemPrice === maxPrice && maxPrice > 0 && minPrice !== maxPrice;
                              
                              let rowBgColor = 'transparent';
                              if (isLowest) rowBgColor = '#dcfce7'; // Light green
                              else if (isHighest) rowBgColor = '#fee2e2'; // Light red
                              
                              return (
                                <tr 
                                  key={item.Id || idx}
                                  style={{ 
                                    borderBottom: '1px solid #e5e7eb',
                                    backgroundColor: rowBgColor,
                                    transition: 'background-color 0.2s'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isLowest && !isHighest) {
                                      e.currentTarget.style.backgroundColor = '#f9fafb';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = rowBgColor;
                                  }}
                                >
                              <td style={{ padding: '12px 16px', fontSize: '0.875rem' }}>
                                {item.FormattedPurchaseDate || new Date(item.PurchaseDate).toLocaleDateString('en-GB')}
                              </td>
                              <td style={{ padding: '12px 16px', fontSize: '0.875rem', fontWeight: 600 }}>
                                {item.PurchaseInvoiceNumber || 'N/A'}
                              </td>
                              <td style={{ padding: '12px 16px', fontSize: '0.875rem' }}>
                                {item.SupplierName || 'N/A'}
                              </td>
                              <td style={{ padding: '12px 16px', fontSize: '0.875rem', textAlign: 'center' }}>
                                <Chip
                                  label={item.HistoryType || 'N/A'}
                                  size="small"
                                  sx={{
                                    bgcolor: item.HistoryType === 'Purchase' ? '#dbeafe' : item.HistoryType === 'Return' ? '#fee2e2' : '#f3f4f6',
                                    color: item.HistoryType === 'Purchase' ? '#1e40af' : item.HistoryType === 'Return' ? '#dc2626' : '#6b7280',
                                    fontWeight: 600,
                                    fontSize: '0.7rem'
                                  }}
                                />
                              </td>
                              <td style={{ padding: '12px 16px', fontSize: '0.875rem', textAlign: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <Box 
                                    sx={{ 
                                      width: 10, 
                                      height: 10, 
                                      borderRadius: '50%',
                                      backgroundColor: item.StoreType === 'Main' ? '#10b981' : '#9ca3af',
                                      boxShadow: item.StoreType === 'Main' ? '0 0 0 2px rgba(16, 185, 129, 0.2)' : '0 0 0 2px rgba(156, 163, 175, 0.2)'
                                    }}
                                    title={item.StoreType || 'Unknown'}
                                  />
                                </Box>
                              </td>
                              <td style={{ padding: '12px 16px', fontSize: '0.875rem', textAlign: 'right' }}>
                                {item.Quantity || 0}
                              </td>
                              <td style={{ padding: '12px 16px', fontSize: '0.875rem', textAlign: 'right' }}>
                                €{(item.UnitCost || 0).toFixed(2)}
                              </td>
                              <td style={{ padding: '12px 16px', fontSize: '0.875rem', textAlign: 'right', color: '#f59e0b' }}>
                                {item.DiscountPercentage ? `${item.DiscountPercentage}%` : '-'}
                              </td>
                              <td style={{ padding: '12px 16px', fontSize: '0.875rem', textAlign: 'right', color: '#ec4899' }}>
                                {item.TaxPercentage ? `${item.TaxPercentage}%` : '-'}
                              </td>
                              <td style={{ padding: '12px 16px', fontSize: '0.875rem', textAlign: 'right', fontWeight: 700, color: '#059669' }}>
                                €{(item.FinalUnitCost || 0).toFixed(2)}
                              </td>
                              <td style={{ padding: '12px 16px', fontSize: '0.875rem', textAlign: 'right', fontWeight: 700 }}>
                                €{(item.TotalCost || 0).toFixed(2)}
                              </td>
                              <td style={{ padding: '12px 16px', fontSize: '0.875rem', textAlign: 'right' }}>
                                {item.PriceChange !== null && item.PriceChange !== undefined ? (
                                  <Chip 
                                    label={`${item.PriceChange > 0 ? '+' : ''}€${item.PriceChange.toFixed(2)}`}
                                    size="small"
                                    sx={{ 
                                      bgcolor: item.PriceChange > 0 ? '#fee2e2' : item.PriceChange < 0 ? '#dcfce7' : '#f3f4f6',
                                      color: item.PriceChange > 0 ? '#dc2626' : item.PriceChange < 0 ? '#16a34a' : '#6b7280',
                                      fontWeight: 600,
                                      fontSize: '0.75rem'
                                    }}
                                  />
                                ) : '-'}
                              </td>
                            </tr>
                          );
                        });
                      })()}
                        </tbody>
                      </table>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </TabPanel>
      {/* Variants Tab */}
        {PRODUCT_VARIANTS_ENABLED && <TabPanel value={currentTab} index={2}>
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight={600}>Product Variants</Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => { setSelectedVariant(null); setVariantDialogOpen(true); }}
              >
                Add Variant
              </Button>
            </Stack>

            {variantsLoading && !productVariantsData ? (
              <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} /></Box>
            ) : (productVariantsData?.variants || []).length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                <InventoryIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
                <Typography variant="body1">No variants yet.</Typography>
                <Typography variant="body2">Add variants to track different versions of this product.</Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {(productVariantsData?.variants || []).map((v) => (
                  <Grid item xs={12} md={6} key={v.id}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body1" fontWeight={600}>{v.variantName}</Typography>
                            <Typography variant="caption" color="text.secondary">SKU: {v.sku}</Typography>
                          </Box>
                          <Stack direction="row" spacing={0.5}>
                            <IconButton size="small" onClick={() => { setSelectedVariant(v); setVariantDialogOpen(true); }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => setConfirmDeleteVariant(v)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Stack>
                        <Grid container spacing={1} sx={{ mt: 1 }}>
                          <Grid item xs={4}>
                            <Typography variant="caption" color="text.secondary" display="block">Sale Price</Typography>
                            <Typography variant="body2" fontWeight={600}>€{Number(v.salePrice || 0).toFixed(2)}</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="caption" color="text.secondary" display="block">Cost</Typography>
                            <Typography variant="body2" fontWeight={600}>€{Number(v.costPrice || 0).toFixed(2)}</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="caption" color="text.secondary" display="block">Stock</Typography>
                            <Chip
                              size="small"
                              label={`${v.availableStock ?? v.stockQuantity ?? 0}`}
                              color={(v.availableStock ?? v.stockQuantity ?? 0) > 0 ? 'success' : 'error'}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                              {v.isSerializedProduct && (
                                <Chip 
                                  size="small" 
                                  label="IMEI / Serial Tracked" 
                                  color="info" 
                                  icon={<QrCodeIcon />} 
                                />
                              )}
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                startIcon={<QrCodeIcon />}
                                onClick={() => {
                                  setSelectedVariantForInventory(v);
                                  setInventoryDialogOpen(true);
                                }}
                              >
                                {v.isSerializedProduct ? 'Manage Items' : 'Track Items (IMEI/Serial)'}
                              </Button>
                            </Stack>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </TabPanel>}
      </Paper>

      {/* Variant Dialogs */}
      {PRODUCT_VARIANTS_ENABLED && <VariantFormDialog
        open={variantDialogOpen}
        onClose={() => setVariantDialogOpen(false)}
        productId={Number(id)}
        variant={selectedVariant}
        onSaved={() => dispatch(fetchVariantsByProduct(id))}
      />}
      {PRODUCT_VARIANTS_ENABLED && <ConfirmationDialog
        open={Boolean(confirmDeleteVariant)}
        title="Delete Variant"
        message={`Delete "${confirmDeleteVariant?.variantName}"? This cannot be undone.`}
        onConfirm={async () => {
          try {
            await dispatch(removeVariant({ id: confirmDeleteVariant.id, productId: Number(id) })).unwrap();
            toast.success('Variant deleted');
          } catch (e) {
            toast.error(e || 'Delete failed');
          } finally {
            setConfirmDeleteVariant(null);
          }
        }}
        onCancel={() => setConfirmDeleteVariant(null)}
        loading={variantsSubmitting}
      />}

      {/* Inventory Items Management Dialog */}
      {PRODUCT_VARIANTS_ENABLED && <InventoryItemsManagementDialog
        open={inventoryDialogOpen}
        onClose={() => {
          setInventoryDialogOpen(false);
          setSelectedVariantForInventory(null);
        }}
        variantId={selectedVariantForInventory?.id}
        variantName={selectedVariantForInventory?.variantName}
        productName={product?.Name || product?.ProductName || product?.name}
      />}

      {/* Barcode Print Dialog */}
      <Dialog 
        open={barcodeDialogOpen} 
        onClose={() => setBarcodeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: 3
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 600, 
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          {t("print_barcode_label") || "Print Barcode Label"}
          <IconButton onClick={() => setBarcodeDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          {/* Label Options */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
              {t("label_options") || "Label Options"}
            </Typography>
            
            {/* Price Checkbox */}
            <FormControlLabel 
              control={
                <Checkbox 
                  checked={includePrice}
                  onChange={(e) => setIncludePrice(e.target.checked)}
                  color="primary"
                />
              }
              label={t("include_price_on_label") || "Include price on label"}
              sx={{ mb: 2, display: 'block' }}
            />

            <Divider sx={{ my: 2 }} />

            {/* Tape / Label Size Selector */}
            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
              <InputLabel>{t("tape_size") || "Tape / Label Size"}</InputLabel>
              <Select
                value={tapeSize}
                label={t("tape_size") || "Tape / Label Size"}
                onChange={(e) => setTapeSize(e.target.value)}
              >
                {TAPE_SIZES.map((ts) => (
                  <MenuItem key={ts.id} value={ts.id}>{ts.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {tapeSize === 'custom' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
                <TextField
                  label="Width (mm)"
                  type="number"
                  value={customWidth}
                  onChange={(e) => setCustomWidth(Math.max(10, Number(e.target.value)))}
                  inputProps={{ min: 10, max: 200 }}
                  size="small"
                  sx={{ width: 120 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={customHeightAuto}
                      onChange={(e) => setCustomHeightAuto(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Auto height (continuous)"
                  sx={{ mx: 0 }}
                />
                {!customHeightAuto && (
                  <TextField
                    label="Height (mm)"
                    type="number"
                    value={customHeightValue}
                    onChange={(e) => setCustomHeightValue(Math.max(10, Number(e.target.value)))}
                    inputProps={{ min: 10, max: 500 }}
                    size="small"
                    sx={{ width: 120 }}
                  />
                )}
              </Box>
            )}
            
            {/* Barcode Type Radio Buttons - Only show if both SKU and Barcode exist */}
            {product && product.SKU && (product.Barcode || product.BarCode) && (
              <>
                <Divider sx={{ my: 2 }} />
                <FormControl component="fieldset" sx={{ width: '100%' }}>
                  <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                    {t("barcode_type") || "Barcode Type"}
                  </FormLabel>
                  <RadioGroup
                    value={barcodeType}
                    onChange={(e) => setBarcodeType(e.target.value)}
                  >
                    <FormControlLabel 
                      value="barcode" 
                      control={<Radio />} 
                      label={`${t("use_barcode") || "Use Barcode"} (${product.Barcode || product.BarCode})`}
                    />
                    <FormControlLabel 
                      value="sku" 
                      control={<Radio />} 
                      label={`${t("use_sku") || "Use SKU"} (${product.SKU})`}
                    />
                  </RadioGroup>
                </FormControl>
              </>
            )}
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          {/* Label Preview */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.primary', textAlign: 'center' }}>
              {t("label_preview") || "Label Preview"} - {t("click_to_edit_name") || "Click name to edit"}
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              justifyContent: 'center',
              gap: includePrice ? 2 : 2.5,
              p: 3,
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              backgroundColor: 'grey.50',
              width: '100%',
              maxWidth: '400px',
              margin: '0 auto',
              minHeight: '250px'
            }}>
              {/* Editable Product Name */}
              {(() => {
                // Get adaptive font size for preview
                const tapeCfg = TAPE_SIZES.find(t => t.id === tapeSize);
                const effectiveW = tapeSize === 'custom' ? (customWidth || 62) : (tapeCfg?.width || 62);
                const effectiveH = tapeSize === 'custom'
                  ? (customHeightAuto ? 'auto' : (customHeightValue || 50))
                  : (tapeCfg?.height ?? 'auto');
                const previewCfg = getLabelConfig(effectiveW, effectiveH, includePrice);
                const previewFontSize = previewCfg.nameFontSize || (includePrice ? '14px' : '16px');
                
                return (
                  <TextField
                    fullWidth
                    multiline
                    maxRows={3}
                    value={editableProductName}
                    onChange={(e) => setEditableProductName(e.target.value)}
                    placeholder={t("enter_product_name") || "Enter product name"}
                    inputProps={{ maxLength: 100, style: { textAlign: 'center' } }}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        fontWeight: 600,
                        fontSize: previewFontSize,
                        backgroundColor: 'white',
                        '& fieldset': {
                          borderColor: 'primary.main',
                          borderWidth: 2,
                        },
                        '&:hover fieldset': {
                          borderColor: 'primary.dark',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'primary.main',
                        },
                      },
                      '& .MuiInputBase-input': {
                        textAlign: 'center',
                      }
                    }}
                  />
                );
              })()}
            
            {/* Barcode - Use selected type */}
            <Box id="barcode-to-print">
              {(() => {
                // Determine which barcode to display
                const hasBarcodeValue = product.Barcode || product.BarCode;
                const displayValue = (barcodeType === 'barcode' && hasBarcodeValue) 
                  ? (product.Barcode || product.BarCode)
                  : product.SKU;

                // Scale barcode to current tape selection
                const tapeCfg = TAPE_SIZES.find(t => t.id === tapeSize);
                const effectiveW = tapeSize === 'custom' ? (customWidth || 62) : (tapeCfg?.width || 62);
                const effectiveH = tapeSize === 'custom'
                  ? (customHeightAuto ? 'auto' : (customHeightValue || 50))
                  : (tapeCfg?.height ?? 'auto');
                const labelCfg = getLabelConfig(effectiveW, effectiveH, includePrice);
                
                if (displayValue) {
                  // Calculate barcode font size based on label width
                  const barcodeFontSize = Math.max(14, Math.round(effectiveW * 0.25));
                  return (
                    <Barcode 
                      value={displayValue}
                      format="CODE128"
                      width={labelCfg.barcodeWidth}
                      height={labelCfg.barcodeHeight}
                      displayValue={true}
                      fontSize={barcodeFontSize}
                      margin={2}
                    />
                  );
                } else {
                  return <Typography color="error">No barcode or SKU available</Typography>;
                }
              })()}
            </Box>
            
            {/* Price - Conditional */}
            {includePrice && (() => {
              // Calculate adaptive price font size (more accommodative, matching print)
              const tapeCfg = TAPE_SIZES.find(t => t.id === tapeSize);
              const effectiveW = tapeSize === 'custom' ? (customWidth || 62) : (tapeCfg?.width || 62);
              const priceFontSize = Math.max(16, Math.round(effectiveW * 0.40));
              
              return (
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'primary.main',
                    fontSize: `${priceFontSize}px`
                  }}
                >
                  €{Number(product.SalePrice || product.Price || 0).toFixed(2)}
                </Typography>
              );
            })()}
          </Box>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              {(() => {
                const tapeCfg = TAPE_SIZES.find(t => t.id === tapeSize);
                const w = tapeSize === 'custom' ? (customWidth || '?') : tapeCfg?.width;
                const h = tapeSize === 'custom'
                  ? (customHeightAuto ? 'auto' : customHeightValue)
                  : tapeCfg?.height;
                const sizeStr = h === 'auto' ? `${w}mm continuous` : `${w}mm × ${h}mm`;
                const isPortrait = h !== 'auto' && typeof h === 'number' && h > w * 1.2;
                const orientation = isPortrait ? ' (portrait/vertical layout)' : '';
                return `Printing on ${sizeStr}${orientation} label. Layout optimized for paper size: Name → Barcode${includePrice ? ' → Price' : ''}.`;
              })()}
            </Typography>
          </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          px: 3, 
          py: 2, 
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          <Button 
            onClick={() => setBarcodeDialogOpen(false)}
            variant="outlined"
            color="secondary"
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            {t("cancel") || "Cancel"}
          </Button>
          <Button 
            onClick={handlePrintBarcode}
            variant="contained"
            color="primary"
            startIcon={<PrintIcon />}
            disabled={!product.Barcode && !product.BarCode && !product.SKU}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              boxShadow: 2
            }}
          >
            {t("print") || "Print"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Add to Wishlist Dialog */}
      <ProductWishlistDialog
        open={wishlistDialogOpen}
        onClose={() => setWishlistDialogOpen(false)}
        product={product}
        onSuccess={() => setWishlistDialogOpen(false)}
      />
    </Container>
  );
};

export default ProductDetailPage;